/**
 * Enemy - AI-controlled hostile entity
 */

import { Entity } from '../entities/Entity';
import { Vector2 } from '../math/Vector2';
import { Vector3 } from '../math/Vector3';
import { Renderer } from '../core/Renderer';
import { HealthComponent } from '../entities/components/HealthComponent';
import { MovementComponent } from '../entities/components/MovementComponent';
import { Player } from '../entities/Player';
import { renderEnemySprite, hasEnemySprite } from './EnemySprites';

export enum AIState {
  Idle,
  Patrol,
  Chase,
  Attack,
  Flee
}

export interface EnemyConfig {
  type: string;
  health: number;
  damage: number;
  speed: number;
  detectionRange: number;
  attackRange: number;
  attackCooldown: number;
  color: string;
  size: number;
  experienceValue: number;
}

export class Enemy extends Entity {
  readonly enemyType: string;
  readonly config: EnemyConfig;

  // Components
  public health: HealthComponent;
  public movement: MovementComponent;

  // AI state
  private aiState: AIState = AIState.Idle;
  private target: Player | null = null;
  private stateTime: number = 0;
  private lastKnownTargetPos: Vector3 | null = null;

  // Attack
  private attackCooldown: number = 0;
  private isAttacking: boolean = false;
  private attackTimer: number = 0;

  // Callbacks
  public onDeath: ((enemy: Enemy) => void) | null = null;

  constructor(position: Vector3, config: EnemyConfig) {
    super(position);
    this.enemyType = config.type;
    this.config = config;

    // Setup components
    this.health = this.addComponent(new HealthComponent(config.health));
    this.movement = this.addComponent(new MovementComponent(config.speed, 8));

    // Health callbacks
    this.health.onDeath = () => this.die();
    this.health.onDamage = () => this.onTakeDamage();
  }

  update(deltaTime: number): void {
    super.update(deltaTime);

    this.stateTime += deltaTime;

    // Update attack cooldown
    if (this.attackCooldown > 0) {
      this.attackCooldown -= deltaTime;
    }

    // Update attack animation
    if (this.isAttacking) {
      this.attackTimer -= deltaTime;
      if (this.attackTimer <= 0) {
        this.isAttacking = false;
      }
    }

    // Run AI state machine
    this.updateAI(deltaTime);
  }

  private updateAI(deltaTime: number): void {
    switch (this.aiState) {
      case AIState.Idle:
        this.handleIdle(deltaTime);
        break;
      case AIState.Chase:
        this.handleChase(deltaTime);
        break;
      case AIState.Attack:
        this.handleAttack(deltaTime);
        break;
      case AIState.Flee:
        this.handleFlee(deltaTime);
        break;
    }
  }

  private handleIdle(_deltaTime: number): void {
    // Check for player in range
    if (this.target && this.canSeeTarget()) {
      this.setState(AIState.Chase);
      return;
    }

    // Random idle movement occasionally
    if (this.stateTime > 3) {
      this.stateTime = 0;
      // Could add patrol behavior here
    }
  }

  private handleChase(_deltaTime: number): void {
    if (!this.target) {
      this.setState(AIState.Idle);
      return;
    }

    // Lost sight of target?
    if (!this.canSeeTarget()) {
      if (this.lastKnownTargetPos) {
        // Move towards last known position
        this.movement.moveTowards(this.lastKnownTargetPos, _deltaTime);
      }
      // Give up after a while
      if (this.stateTime > 5) {
        this.clearTarget();
        this.setState(AIState.Idle);
      }
      return;
    }

    // Update last known position
    this.lastKnownTargetPos = this.target.position.clone();

    // In attack range?
    if (this.isTargetInRange()) {
      this.setState(AIState.Attack);
      return;
    }

    // Move towards target
    this.movement.moveTowards(this.target.position, _deltaTime);
  }

  private handleAttack(_deltaTime: number): void {
    if (!this.target) {
      this.setState(AIState.Idle);
      return;
    }

    // Out of range? Chase again
    if (!this.isTargetInRange()) {
      this.setState(AIState.Chase);
      return;
    }

    // Face target
    const dx = this.target.position.x - this.position.x;
    const dy = this.target.position.y - this.position.y;
    this.movement.setFacingDirection(new Vector2(dx, dy));

    // Attack if cooldown ready
    if (this.attackCooldown <= 0) {
      this.performAttack();
    }
  }

  private handleFlee(_deltaTime: number): void {
    if (!this.target) {
      this.setState(AIState.Idle);
      return;
    }

    // Run away from target
    const dx = this.position.x - this.target.position.x;
    const dy = this.position.y - this.target.position.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist > 0) {
      this.movement.move(new Vector2(dx / dist, dy / dist));
    }

    // Stop fleeing if far enough
    if (dist > this.config.detectionRange * 1.5) {
      this.clearTarget();
      this.setState(AIState.Idle);
    }
  }

  private performAttack(): void {
    if (!this.target) return;
    // Can't attack player if they're flying high
    if (this.target.isSafeFromEnemies()) return;

    this.isAttacking = true;
    this.attackTimer = 0.3;
    this.attackCooldown = this.config.attackCooldown;

    // Deal damage to player
    this.target.health.takeDamage(this.config.damage);
  }

  setTarget(player: Player): void {
    this.target = player;
    this.lastKnownTargetPos = player.position.clone();
    this.setState(AIState.Chase);
  }

  clearTarget(): void {
    this.target = null;
    this.lastKnownTargetPos = null;
  }

  canSeeTarget(): boolean {
    if (!this.target) return false;
    // Can't see player if they're flying high
    if (this.target.isSafeFromEnemies()) return false;
    const dist = this.distanceTo(this.target);
    return dist <= this.config.detectionRange;
  }

  isTargetInRange(): boolean {
    if (!this.target) return false;
    // Can't reach player if they're flying high
    if (this.target.isSafeFromEnemies()) return false;
    const dist = this.distanceTo(this.target);
    return dist <= this.config.attackRange;
  }

  private setState(state: AIState): void {
    if (this.aiState !== state) {
      this.aiState = state;
      this.stateTime = 0;
    }
  }

  getState(): AIState {
    return this.aiState;
  }

  private die(): void {
    this.onDeath?.(this);
    this.destroy();
  }

  private onTakeDamage(): void {
    // Flash effect, aggro, etc.
    this.health.setInvulnerable(0.2);

    // If we weren't already aggroed, flee or fight based on health
    if (this.aiState === AIState.Idle) {
      if (this.health.getHealthPercent() < 0.3) {
        this.setState(AIState.Flee);
      } else if (this.target) {
        this.setState(AIState.Chase);
      }
    }
  }

  render(renderer: Renderer): void {
    // Shadow
    renderer.drawCircle(
      new Vector3(this.position.x, this.position.y, 0),
      this.config.size / 3,
      'rgba(0, 0, 0, 0.3)'
    );

    // Try to render pixel art sprite
    if (hasEnemySprite(this.enemyType)) {
      const screenPos = renderer.worldToScreen(this.position);
      const spriteSize = this.config.size * 2 * renderer.getZoom();

      renderEnemySprite(
        renderer.getContext(),
        this.enemyType,
        screenPos.x,
        screenPos.y,
        spriteSize,
        this.isAttacking
      );
    } else {
      // Fallback to simple rectangle
      const bodyColor = this.isAttacking ? '#ff6666' : this.config.color;
      renderer.drawRect(
        this.position,
        this.config.size,
        this.config.size * 1.2,
        bodyColor
      );

      // Eyes (facing direction)
      const facing = this.movement.getFacingDirection();
      const eyeOffset = this.config.size * 0.2;
      renderer.drawCircle(
        new Vector3(
          this.position.x + facing.x * eyeOffset * 0.3,
          this.position.y + facing.y * eyeOffset * 0.3,
          this.config.size
        ),
        3,
        '#ff0000'
      );
    }

    // Health bar
    if (this.health.getHealthPercent() < 1) {
      renderer.drawHealthBar(
        new Vector3(this.position.x, this.position.y, this.config.size * 1.5),
        this.health.getCurrentHealth(),
        this.health.getMaxHealth(),
        30,
        4
      );
    }

    // State indicator (debug)
    // renderer.drawTextWorld(AIState[this.aiState], new Vector3(this.position.x, this.position.y, this.config.size * 2), '#fff', '10px monospace');
  }
}

// Predefined enemy types
export const ENEMY_CONFIGS: { [key: string]: EnemyConfig } = {
  zombie: {
    type: 'zombie',
    health: 50,
    damage: 10,
    speed: 2,
    detectionRange: 8,
    attackRange: 1.5,
    attackCooldown: 1.5,
    color: '#5d8a5d',
    size: 20,
    experienceValue: 10
  },
  skeleton: {
    type: 'skeleton',
    health: 30,
    damage: 15,
    speed: 3,
    detectionRange: 12,
    attackRange: 1.5,
    attackCooldown: 1.2,
    color: '#d4d4d4',
    size: 18,
    experienceValue: 15
  },
  spider: {
    type: 'spider',
    health: 20,
    damage: 8,
    speed: 5,
    detectionRange: 6,
    attackRange: 1.2,
    attackCooldown: 0.8,
    color: '#4a4a4a',
    size: 16,
    experienceValue: 8
  },
  wolf: {
    type: 'wolf',
    health: 35,
    damage: 12,
    speed: 6,
    detectionRange: 14,
    attackRange: 1.3,
    attackCooldown: 0.6,
    color: '#7a7a8a',
    size: 18,
    experienceValue: 12
  },
  orc: {
    type: 'orc',
    health: 100,
    damage: 25,
    speed: 2.5,
    detectionRange: 10,
    attackRange: 2,
    attackCooldown: 2,
    color: '#4a7a4a',
    size: 28,
    experienceValue: 30
  },
  golem: {
    type: 'golem',
    health: 200,
    damage: 35,
    speed: 1.5,
    detectionRange: 6,
    attackRange: 2.5,
    attackCooldown: 3,
    color: '#8a7a6a',
    size: 35,
    experienceValue: 50
  },
  demon: {
    type: 'demon',
    health: 150,
    damage: 40,
    speed: 4,
    detectionRange: 16,
    attackRange: 2,
    attackCooldown: 1.5,
    color: '#aa3333',
    size: 30,
    experienceValue: 75
  },
  ghost: {
    type: 'ghost',
    health: 40,
    damage: 20,
    speed: 4.5,
    detectionRange: 12,
    attackRange: 1.5,
    attackCooldown: 1,
    color: '#aaccff',
    size: 22,
    experienceValue: 25
  }
};
